import i18next from 'i18next';

export type TransactionStatusDisplay = {
  label: string;
  badgeClass: string;
  icon: string;
  toneClass: string;
};

const statusLabel = (key: string, fallback: string): string => {
  const full = `transactionStatus.${key}`;
  const value = i18next.t(full);
  // When i18next is not initialised (e.g. unit tests) it returns the key or
  // undefined; fall back to the Swedish default so behaviour is unchanged there.
  return typeof value === 'string' && value !== full ? value : fallback;
};

const oreToSek = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.round(value / 100);
};

const numberOrNull = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
};

export const transactionStatusDisplay = (status: unknown): TransactionStatusDisplay => {
  switch (status) {
    case 'released':
      return { label: statusLabel('released', 'Utbetald till hantverkare'), badgeClass: 'bg-success', icon: 'check-circle', toneClass: 'bg-success' };
    case 'pending':
    case 'releasing':
      return { label: statusLabel('pending', 'Pågår'), badgeClass: 'bg-warning text-dark', icon: 'clock', toneClass: 'bg-warning' };
    case 'failed':
      return { label: statusLabel('failed', 'Misslyckades'), badgeClass: 'bg-danger', icon: 'x', toneClass: 'bg-danger' };
    case 'cancelled':
      return { label: statusLabel('cancelled', 'Annullerad'), badgeClass: 'bg-secondary', icon: 'x-circle', toneClass: 'bg-secondary' };
    case 'partially_refunded':
      return { label: statusLabel('partiallyRefunded', 'Delvis återbetald'), badgeClass: 'bg-warning text-dark', icon: 'corner-up-left', toneClass: 'bg-warning' };
    case 'refunded':
      return { label: statusLabel('refunded', 'Återbetald'), badgeClass: 'bg-secondary', icon: 'rotate-ccw', toneClass: 'bg-secondary' };
    case 'disputed':
      return { label: statusLabel('disputed', 'Chargeback/tvist'), badgeClass: 'bg-danger', icon: 'alert-triangle', toneClass: 'bg-danger' };
    case 'expired':
      return { label: statusLabel('expired', 'Utgången'), badgeClass: 'bg-secondary', icon: 'x-circle', toneClass: 'bg-secondary' };
    case 'held':
    case 'completed':
    default:
      return { label: statusLabel('held', 'Spärrat belopp'), badgeClass: 'bg-info', icon: 'lock', toneClass: 'bg-info' };
  }
};

export const transactionReleasedToBuilder = (txn: Record<string, unknown>): boolean => {
  if (['cancelled', 'partially_refunded', 'refunded', 'disputed'].includes(String(txn.status))) {
    return false;
  }
  return (
    txn.status === 'released' ||
    txn.releasedAt != null ||
    txn.released_at != null ||
    txn.payoutReference != null ||
    txn.payout_reference != null
  );
};

export const transactionBaseAmountSek = (txn: Record<string, unknown>): number => {
  return (
    oreToSek(txn.baseAmountOre) ??
    numberOrNull(txn.projectValueSek) ??
    numberOrNull(txn.projectValue) ??
    0
  );
};

export const transactionTotalAmountSek = (txn: Record<string, unknown>): number => {
  return (
    oreToSek(txn.amountOre) ??
    numberOrNull(txn.totalPaidSek) ??
    numberOrNull(txn.totalPaid) ??
    transactionBaseAmountSek(txn)
  );
};

export const transactionPayoutAmountSek = (txn: Record<string, unknown>): number => {
  return (
    oreToSek(txn.netPayoutOre) ??
    numberOrNull(txn.netPayoutSek) ??
    numberOrNull(txn.netPayout) ??
    transactionBaseAmountSek(txn)
  );
};

export const transactionCustomerFeeSek = (txn: Record<string, unknown>): number => {
  const fromOre = oreToSek(txn.platformFeeOre);
  if (fromOre !== null) return Math.max(fromOre, 0);

  const total = transactionTotalAmountSek(txn);
  const base = transactionBaseAmountSek(txn);
  return Math.max(total - base, 0);
};

export const transactionBaseAmountLabelKey = (
  txn: Record<string, unknown>,
  options: { contractMilestoneCount?: number | null } = {}
): 'contractAmount' | 'milestoneAmount' => {
  const hasMilestoneReference =
    txn.milestoneId != null ||
    txn.milestone_id != null ||
    txn.milestoneNumber != null ||
    txn.milestone_number != null;
  return hasMilestoneReference && (options.contractMilestoneCount ?? 0) > 1
    ? 'milestoneAmount'
    : 'contractAmount';
};

export const shouldShowIncomingDirection = (
  txn: Record<string, unknown>,
  options: { isPerson: boolean }
): boolean => {
  return !(options.isPerson && txn.direction !== 'outgoing');
};
